"use client";

import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { CalendarClock, Gavel, HandCoins, Package, Search, Users } from "lucide-react";

import { Countdown } from "@/components/ui/countdown";
import { SellerRow } from "@/components/feed/seller-row";
import { OfferModal } from "@/components/offers/offer-modal";
import AuthModal from "@/components/ui/auth-modal";
import { CommerceIcon } from "@/lib/icons/commerce-icons";
import {
  RailSectionSkeleton,
  RailSellerRowSkeleton,
} from "@/components/feed/discovery-rail-skeleton";
import { usePaginatedFeed, type FeedFilterState, type ListingItem } from "@/hooks/use-paginated-feed";
import { useSuggestedSellers } from "@/hooks/use-suggested-sellers";
import {
  deriveSavedItemAction,
  effectiveSavedListingPrice,
  primarySavedListingImage,
  savedActionLook,
} from "@/lib/saved-item-row";
import { SQUARE_SM, imagePresetUrl } from "@/lib/images/presets";
import { feedListingToSavedListing } from "@/lib/feed/discovery-rail";
import { cn, formatPrice } from "@/lib/utils";

/**
 * DiscoveryRail — the desktop feed's right rail (mockup: Kraahl Desktop Home,
 * 320px). Logged-out: a foil "Join Kraahl" card. Then the mockup's discovery
 * sections built from the feed data + suggested-users: Open to offers, Upcoming
 * pre-orders, Sellers to discover. Reuses existing components — no item cards
 * touched. Each section collapses when it has nothing to show.
 *
 * TODO(v3): dedicated queries per section (currently filtered from just-listed);
 * foil the Follow button with the seller-card consolidation slice.
 */

const OFFER_FORMATS = new Set(["offers_only", "fixed_price_with_offers"]);

function priceLabel(l: ListingItem) {
  return formatPrice(l.price, (l.currency || "USD").toUpperCase());
}

function Thumb({ urls }: { urls?: string[] | null }) {
  const raw = primarySavedListingImage(urls ?? undefined);
  if (!raw) {
    return (
      <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-muted">
        <Package className="size-5 text-muted-foreground" />
      </div>
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={imagePresetUrl(raw, SQUARE_SM)}
      alt=""
      className="size-12 shrink-0 rounded-lg object-cover"
      loading="lazy"
      decoding="async"
    />
  );
}

function daysUntil(dateStr: string): number {
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
}

function SectionHeader({ icon: Icon, title }: { icon: typeof Users; title: string }) {
  return (
    <div className="mb-3.5 flex items-center gap-2">
      <Icon className="size-4 shrink-0 text-foreground" strokeWidth={2} />
      <span className="text-[15px] font-bold tracking-tight">{title}</span>
    </div>
  );
}

/**
 * Compact "Open to offers" row. Reuses the saved-item-row domain
 * (`deriveSavedItemAction` + `savedActionLook`) and the canonical `OfferModal`
 * so the trailing affordance is a real derived CTA (Offer / Find similar) rather
 * than a passive "Listed" label — while keeping the rail's compact 48px layout.
 */
function OfferRow({
  listing,
  onOpen,
  onOffer,
}: {
  listing: ListingItem;
  onOpen: (id: string) => void;
  onOffer: (listing: ListingItem) => void;
}) {
  const router = useRouter();
  const t = useTranslations("savedPage");
  const mapped = feedListingToSavedListing(listing);
  const action = deriveSavedItemAction(mapped);
  const look = savedActionLook(action);
  const price = formatPrice(
    effectiveSavedListingPrice(mapped),
    (listing.currency || "USD").toUpperCase(),
  );

  const onAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (action === "offer") {
      onOffer(listing);
      return;
    }
    if (action === "find_similar") {
      router.push(`/search?q=${encodeURIComponent(listing.title)}`);
      return;
    }
    onOpen(listing.id);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(listing.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(listing.id);
        }
      }}
      className="flex cursor-pointer items-center gap-3 rounded-2xl p-2.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={listing.title}
    >
      <Thumb urls={listing.image_urls} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold leading-tight">{listing.title}</div>
        <div className="mt-1 text-[13px] font-bold tabular-nums">{price}</div>
      </div>
      <button
        type="button"
        onClick={onAction}
        className={cn(
          "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[12px] font-medium transition-transform active:scale-95",
          // Foil the offer CTA (the ONE action for these rows) — same holographic
          // material as the item-card Buy/Offer button; the icon + label inherit
          // foil-ink. Other derived actions keep their solid look.
          action === "offer"
            ? "overflow-hidden foil-fill foil-emboss foil-ink foil-sweep hover:brightness-105"
            : look.className,
        )}
      >
        {look.icon === "search" ? (
          <Search className="size-3.5 shrink-0" />
        ) : (
          <CommerceIcon name={look.icon} className="size-3.5 shrink-0" />
        )}
        {t(look.labelKey)}
      </button>
    </div>
  );
}

/** Compact "Auctions ending soon" row: current bid + bid count + a self-ticking
 *  amber/ember countdown pill (canonical `Countdown` card variant). */
function AuctionRow({
  listing,
  onOpen,
}: {
  listing: ListingItem;
  onOpen: (id: string) => void;
}) {
  const bid = listing.auction_highest_bid ?? listing.auction_start_price ?? null;
  const bidText =
    bid != null ? formatPrice(bid, (listing.currency || "USD").toUpperCase()) : "—";
  const count = listing.auction_bid_count ?? 0;
  const bidsText = count === 0 ? "No bids" : `${count} bid${count === 1 ? "" : "s"}`;

  return (
    <button
      type="button"
      onClick={() => onOpen(listing.id)}
      className="flex items-center gap-3 rounded-2xl p-2.5 text-left transition-colors hover:bg-accent"
    >
      <Thumb urls={listing.image_urls} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold leading-tight">{listing.title}</div>
        <div className="mt-1 flex items-center gap-1.5 text-[13px]">
          <span className="font-bold tabular-nums">{bidText}</span>
          <span className="text-[11px] text-muted-foreground">{bidsText}</span>
        </div>
      </div>
      {listing.auction_end_at ? (
        <Countdown endsAt={listing.auction_end_at} variant="card" className="shrink-0" />
      ) : null}
    </button>
  );
}

export function DiscoveryRail({
  currentUserId,
  className,
}: {
  currentUserId?: string;
  className?: string;
}) {
  const router = useRouter();
  const { items, isLoading } = usePaginatedFeed({
    userId: currentUserId,
    mode: "just-listed",
    limit: 30,
    maxItems: 30,
  });
  const t = useTranslations();
  const [offerTarget, setOfferTarget] = useState<ListingItem | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const feedLoading = isLoading && items.length === 0;

  // Making an offer requires an account — gate guests to the login dialog first,
  // exactly like the listing offer CTA (make-offer-button.tsx).
  const handleOffer = (listing: ListingItem) => {
    if (!currentUserId) {
      setAuthOpen(true);
      return;
    }
    setOfferTarget(listing);
  };

  // Auctions ending soon need their own query: an auction that ends soon is an
  // *old* listing, so the just-listed feed almost never contains it. Filter the
  // feed to auction format, then sort by soonest end client-side (active-auction
  // count is small; one page covers them).
  const auctionFilters = useMemo<FeedFilterState>(
    () => ({
      brands: [],
      themes: [],
      categories: [],
      conditions: [],
      gradeRange: null,
      listingFormats: ["auction"],
    }),
    [],
  );
  const { items: auctionItems, isLoading: auctionsLoadingRaw } = usePaginatedFeed({
    userId: currentUserId,
    mode: "just-listed",
    limit: 40,
    maxItems: 40,
    filters: auctionFilters,
  });
  const auctionsEndingSoon = auctionItems
    .filter((l) => {
      const status = String(l.auction_status ?? "").toLowerCase();
      if (status === "ended" || status === "settled" || status === "cancelled") return false;
      return Boolean(l.auction_end_at) && new Date(l.auction_end_at as string).getTime() > Date.now();
    })
    .sort(
      (a, b) =>
        new Date(a.auction_end_at as string).getTime() - new Date(b.auction_end_at as string).getTime(),
    )
    .slice(0, 3);
  const auctionsLoading = auctionsLoadingRaw && auctionItems.length === 0;

  const openToOffers = items
    .filter((l) => OFFER_FORMATS.has(l.listing_format ?? ""))
    .slice(0, 4);
  const preorders = items
    .filter((l) => l.type === "preorder" && l.scheduled_publish_at && daysUntil(l.scheduled_publish_at) > 0)
    .slice(0, 3);

  const sellersState = useSuggestedSellers({ excludeProfileId: currentUserId, limit: 4 });

  const open = (id: string) => router.push(`/listings/${id}`);

  return (
    <aside
      className={cn(
        "hide-scrollbar h-full w-80 shrink-0 space-y-7 overflow-y-auto border-l border-border/60 px-5 pb-24 pt-6",
        className,
      )}
    >
      {auctionsLoading ? (
        <div>
          <SectionHeader icon={Gavel} title="Auctions ending soon" />
          <RailSectionSkeleton count={3} />
        </div>
      ) : auctionsEndingSoon.length > 0 ? (
        <div>
          <SectionHeader icon={Gavel} title="Auctions ending soon" />
          <div className="flex flex-col gap-1.5">
            {auctionsEndingSoon.map((l) => (
              <AuctionRow key={l.id} listing={l} onOpen={open} />
            ))}
          </div>
        </div>
      ) : null}

      {feedLoading ? (
        <div>
          <SectionHeader icon={HandCoins} title="Open to offers" />
          <RailSectionSkeleton count={4} />
        </div>
      ) : openToOffers.length > 0 ? (
        <div>
          <SectionHeader icon={HandCoins} title="Open to offers" />
          <div className="flex flex-col gap-1.5">
            {openToOffers.map((l) => (
              <OfferRow key={l.id} listing={l} onOpen={open} onOffer={handleOffer} />
            ))}
          </div>
        </div>
      ) : null}

      {preorders.length > 0 ? (
        <div>
          <SectionHeader icon={CalendarClock} title="Upcoming pre-orders" />
          <div className="flex flex-col gap-1.5">
            {preorders.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => open(l.id)}
                className="flex items-center gap-3 rounded-2xl p-2.5 text-left transition-colors hover:bg-accent"
              >
                <Thumb urls={l.image_urls} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-semibold leading-tight">{l.title}</div>
                  <div className="mt-1 text-[13px] font-bold tabular-nums">{priceLabel(l)}</div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-secondary px-2 py-1 text-[11.5px] font-bold text-muted-foreground">
                  <CalendarClock className="size-3" strokeWidth={2.2} />
                  {daysUntil(l.scheduled_publish_at as string)}d
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {sellersState.kind === "loading" ? (
        <div>
          <SectionHeader icon={Users} title="Sellers to discover" />
          <div className="flex flex-col gap-2.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <RailSellerRowSkeleton key={i} />
            ))}
          </div>
        </div>
      ) : sellersState.kind === "ready" && sellersState.sellers.length > 0 ? (
        <div>
          <SectionHeader icon={Users} title="Sellers to discover" />
          <div className="flex flex-col gap-2.5">
            {sellersState.sellers.map((s) => (
              <SellerRow key={s.id} seller={s} currentUserId={currentUserId} />
            ))}
          </div>
        </div>
      ) : null}

      <OfferModal
        open={offerTarget !== null}
        onOpenChange={(open) => {
          if (!open) setOfferTarget(null);
        }}
        mode={{
          kind: "create",
          listingId: offerTarget?.id ?? "",
          listingPrice:
            offerTarget && offerTarget.listing_format !== "offers_only"
              ? Number.parseFloat(offerTarget.price)
              : null,
          currency: offerTarget?.currency ?? null,
        }}
        listingPreview={{
          title: offerTarget?.title ?? "",
          imageUrl: offerTarget?.image_urls?.[0] ?? null,
          condition: offerTarget?.condition ?? null,
          grade: offerTarget?.grade ?? null,
        }}
      />

      <AuthModal
        isOpen={authOpen}
        onCloseAction={() => setAuthOpen(false)}
        message={t("offers.authPrompt", {
          default: "Please log in or register to make an offer.",
        })}
      />
    </aside>
  );
}
